/*
  Sidebar mega menu panel switching.

  The left column is a vertical tablist; the right shows the active tab's
  panel. Whether hovering a category switches the panel is a merchant setting
  — hover is quicker to browse, click is steadier and better on touch — but
  clicking and keyboard always work regardless, so the menu is never
  dependent on a pointer.

  Delegated from the document because mega menus are re-rendered whenever the
  header section is edited in the theme editor.
*/

const TAB = '[data-mega-tab]';

class MegaMenuController {
  constructor() {
    this.onOver = this.onOver.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    document.addEventListener('pointerover', this.onOver);
    document.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  /* ---------- activation ---------- */

  activate(tab, { focus = false } = {}) {
    const root = tab.closest('[data-mega-menu]');
    if (!root) return;

    const tabs = Array.from(root.querySelectorAll(TAB));
    tabs.forEach((other) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', selected ? 'true' : 'false');
      // Roving tabindex: only the active tab is in the tab order, so Tab
      // moves past the whole group rather than through every category.
      other.setAttribute('tabindex', selected ? '0' : '-1');

      const panel = root.querySelector(`#${CSS.escape(other.getAttribute('aria-controls'))}`);
      if (panel) panel.hidden = !selected;
    });

    if (focus) tab.focus();
  }

  /* ---------- pointer ---------- */

  onOver(event) {
    const tab = event.target.closest(TAB);
    if (!tab) return;
    const root = tab.closest('[data-mega-menu]');
    if (root?.dataset.megaSwitch !== 'hover') return;
    this.activate(tab);
  }

  onClick(event) {
    const tab = event.target.closest(TAB);
    if (!tab) return;

    // A category heading is also a destination. One click reveals its
    // products; clicking the already-active category follows through to the
    // category page, so the link is still reachable.
    const alreadyActive = tab.getAttribute('aria-selected') === 'true';
    const href = tab.getAttribute('data-mega-href');

    if (alreadyActive && href) {
      window.location.href = href;
      return;
    }
    this.activate(tab);
  }

  /* ---------- keyboard ---------- */

  onKeydown(event) {
    const tab = event.target.closest(TAB);
    if (!tab) return;

    const root = tab.closest('[data-mega-menu]');
    if (!root) return;
    const tabs = Array.from(root.querySelectorAll(TAB));
    const index = tabs.indexOf(tab);
    if (index === -1) return;

    let next = null;
    switch (event.key) {
      case 'ArrowDown':
        next = tabs[(index + 1) % tabs.length];
        break;
      case 'ArrowUp':
        next = tabs[(index - 1 + tabs.length) % tabs.length];
        break;
      case 'Home':
        next = tabs[0];
        break;
      case 'End':
        next = tabs[tabs.length - 1];
        break;
      case 'Enter':
      case ' ': {
        const href = tab.getAttribute('data-mega-href');
        if (href) {
          event.preventDefault();
          window.location.href = href;
        }
        return;
      }
      default:
        return;
    }

    event.preventDefault();
    this.activate(next, { focus: true });
  }
}

new MegaMenuController();
